import { Link, useForm } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { useState } from "react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"

interface Option {
  id: number
  name: string
}
interface PaymentMethod {
  value: string
  label: string
}
interface Props {
  activity: Record<string, unknown>
  activityTypes: Option[]
  users: Option[]
  paymentMethods?: PaymentMethod[]
  errors?: Record<string, string[]>
  isEdit?: boolean
}

export default function ActivityForm({
  activity,
  activityTypes,
  users,
  paymentMethods,
  errors,
  isEdit,
}: Props) {
  const [followupOpen, setFollowupOpen] = useState(false)
  const [expenseOpen, setExpenseOpen] = useState(false)

  const { data, setData, post, patch, processing, transform } = useForm({
    title: (activity.title as string | undefined) || "",
    notes: (activity.notes as string | undefined) || "",
    marketing_activity_type_id:
      (activity.marketing_activity_type_id as string | undefined) || "",
    activity_date: (activity.activity_date as string | undefined) || "",
    end_date: (activity.end_date as string | undefined) || "",
    manager_id: (activity.manager_id as string | undefined) || "",
    send_reminder: (activity.send_reminder as boolean | undefined) ?? false,
    is_hpi_coordinated:
      (activity.is_hpi_coordinated as boolean | undefined) ?? false,
    employee_ids: (activity.employee_ids as number[] | undefined) || [],
    followup_days: 0,
    followup_employee_id: "",
    followup_title: "Send follow up email",
    followup_notes: "",
    expense_date: "",
    expense_paid_to: "",
    expense_notes: "",
    expense_paid_by: "",
    expense_amount: "",
    expense_preapproved_by: "",
  })

  const toggleEmployee = (id: number) => {
    const ids = data.employee_ids.includes(id)
      ? data.employee_ids.filter((i) => i !== id)
      : [...data.employee_ids, id]
    setData("employee_ids", ids)
  }

  transform((data) => ({ marketing_activity: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      patch(`/marketing_activities/${String(activity.id)}`)
    } else {
      post("/marketing_activities")
    }
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl space-y-4">
      {/* Row 1: Activity Type + Activity Date */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-medium">Activity Type</label>
          <select
            value={data.marketing_activity_type_id}
            onChange={(e) =>
              setData("marketing_activity_type_id", e.target.value)
            }
            className="w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {activityTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-medium">Activity Date</label>
          <input
            type="datetime-local"
            value={data.activity_date}
            onChange={(e) => setData("activity_date", e.target.value)}
            className="w-full rounded border px-3 py-2"
          />
        </div>
      </div>

      {/* Row 2: End Date + Team Leader */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-medium">
            End Date{" "}
            <span className="text-sm font-normal text-gray-400">
              Leave blank if N/A
            </span>
          </label>
          <input
            type="datetime-local"
            value={data.end_date}
            onChange={(e) => setData("end_date", e.target.value)}
            className="w-full rounded border px-3 py-2"
          />
        </div>
        <div>
          <label className="block font-medium">Team Leader</label>
          <select
            value={data.manager_id}
            onChange={(e) => setData("manager_id", e.target.value)}
            className="w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block font-medium">Title</label>
        <input
          type="text"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          className="w-full rounded border px-3 py-2"
        />
        {errors?.title && (
          <p className="text-hpi-red text-sm">{errors.title.join(", ")}</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block font-medium">Notes</label>
        <textarea
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          className="w-full rounded border px-3 py-2"
          rows={4}
        />
      </div>

      {/* Send Email Reminders */}
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.send_reminder}
            onChange={(e) => setData("send_reminder", e.target.checked)}
          />
          <span className="font-medium">Send Email Reminders</span>
        </label>
      </div>

      {/* HPI Attendees — 2-column grid */}
      <div>
        <label className="block font-medium">HPI Attendees</label>
        {users.length > 0 ? (
          <div className="border-hpi-light-gray grid max-h-60 grid-cols-2 gap-x-4 gap-y-1 overflow-y-auto rounded border p-3">
            {users.map((u) => (
              <label key={u.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.employee_ids.includes(u.id)}
                  onChange={() => toggleEmployee(u.id)}
                />
                <span className="text-sm">{u.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No employees available</p>
        )}
      </div>

      {/* Schedule Follow-up (new only, collapsed by default) */}
      {!isEdit && (
        <Collapsible open={followupOpen} onOpenChange={setFollowupOpen}>
          <div className="rounded border">
            <CollapsibleTrigger className="flex w-full items-center gap-2 px-4 py-3 text-left font-medium hover:bg-gray-50">
              <ChevronRight
                className={`h-4 w-4 transition-transform ${followupOpen ? "rotate-90" : ""}`}
              />
              Schedule Follow-up
              <span className="text-sm font-normal text-gray-400">
                (optional)
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-3 border-t px-4 pt-3 pb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">
                      # Days after Event
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={data.followup_days}
                      onChange={(e) =>
                        setData("followup_days", parseInt(e.target.value) || 0)
                      }
                      className="w-full rounded border px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Assign to
                    </label>
                    <select
                      value={data.followup_employee_id}
                      onChange={(e) =>
                        setData("followup_employee_id", e.target.value)
                      }
                      className="w-full rounded border px-3 py-2"
                    >
                      <option value="">Team Leader (default)</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    Short Description
                  </label>
                  <input
                    type="text"
                    value={data.followup_title}
                    onChange={(e) => setData("followup_title", e.target.value)}
                    className="w-full rounded border px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    Instructions
                  </label>
                  <textarea
                    value={data.followup_notes}
                    onChange={(e) => setData("followup_notes", e.target.value)}
                    className="w-full rounded border px-3 py-2"
                    rows={2}
                  />
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {/* Expense (new only, collapsed by default) */}
      {!isEdit && paymentMethods && (
        <Collapsible open={expenseOpen} onOpenChange={setExpenseOpen}>
          <div className="rounded border">
            <CollapsibleTrigger className="flex w-full items-center gap-2 px-4 py-3 text-left font-medium hover:bg-gray-50">
              <ChevronRight
                className={`h-4 w-4 transition-transform ${expenseOpen ? "rotate-90" : ""}`}
              />
              Add Expense
              <span className="text-sm font-normal text-gray-400">
                (optional)
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-3 border-t px-4 pt-3 pb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">
                      Expense Date
                    </label>
                    <input
                      type="date"
                      value={data.expense_date}
                      onChange={(e) => setData("expense_date", e.target.value)}
                      className="w-full rounded border px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Paid To</label>
                    <input
                      type="text"
                      value={data.expense_paid_to}
                      onChange={(e) =>
                        setData("expense_paid_to", e.target.value)
                      }
                      className="w-full rounded border px-3 py-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">
                      Payment Method
                    </label>
                    <select
                      value={data.expense_paid_by}
                      onChange={(e) =>
                        setData("expense_paid_by", e.target.value)
                      }
                      className="w-full rounded border px-3 py-2"
                    >
                      <option value="">Select...</option>
                      {paymentMethods.map((pm) => (
                        <option key={pm.value} value={pm.value}>
                          {pm.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Amount ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={data.expense_amount}
                      onChange={(e) =>
                        setData("expense_amount", e.target.value)
                      }
                      className="w-full rounded border px-3 py-2"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    Description
                  </label>
                  <input
                    type="text"
                    value={data.expense_notes}
                    onChange={(e) => setData("expense_notes", e.target.value)}
                    className="w-full rounded border px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">
                    Approved by
                  </label>
                  <input
                    type="text"
                    value={data.expense_preapproved_by}
                    onChange={(e) =>
                      setData("expense_preapproved_by", e.target.value)
                    }
                    className="w-full rounded border px-3 py-2"
                  />
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      <div className="flex gap-4">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {isEdit ? "Update" : "Create"}
        </button>
        <Link
          href="/marketing_activities"
          className="hover:bg-hpi-pale-gray rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
